import { menuSectionFor } from '../../../../services/nav/menu-sections';
import { PageValidationType } from '../../../../models/projects/validation.models';
import { Component, ViewChild } from '@angular/core';
import { App, IonicPage, NavController, NavParams } from 'ionic-angular';
import { DailyLogPage } from '../../../daily-log/daily-log/daily-log';
import { NavDailyLogSubmenuPage } from '../nav-daily-log-submenu/nav-daily-log-submenu';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { LogEventsPage } from '../../../daily-log/log-events/log-events';
import { AuthService } from '../../../../_foundation/services/auth/auth.service';
import { LockedDayPermissions, SidebarPermissions } from '../../../../models/permissions/permission.models';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { DailyLogSummaryPage } from '../../../daily-log/daily-log-summary/daily-log-summary';
import { LoginPage } from '../../../auth/login/login';
import { DprSectionsEventsPage } from '../nav-dpr-sections-submenu/dpr-sections-events/dpr-sections-events';
import { NavDprSectionsSubmenuPage } from '../nav-dpr-sections-submenu/nav-dpr-sections-submenu';
import { VoyageLogPage } from '../../../voyage-log/voyage-log/voyage-log';
import { MorningMinutesPage } from '../../../morning-minutes/morning-minutes/morning-minutes';
import { ImplementerNavigatorPage } from '../../../implementer/implementer-navigator/implementer-navigator';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { AppConfig } from '../../../../app/app.config';
import { IdleService } from '../../../../services/idle/idle.service';
import { User } from '../../../../_foundation/models/security.models';
import { cloneDeep } from 'lodash';
import {JackingActivitiesRouterPage} from '../../../jacking-activities/jacking-activities-router/jacking-activities-router-page';
import { VesselService } from '../../../../services/vessel/vessel.service';
import { SupportContextKey } from '../../../../models/support/support.models';
import { SupportContextService } from '../../../../services/support/support-context.service';
import { NavSettingsSubmenuPage } from '../nav-settings-submenu/nav-settings-submenu';

@IonicPage()
@Component({
  selector: 'page-nav-main-menu',
  templateUrl: 'nav-main-menu.html',
})
export class NavMainMenuPage {
  @ViewChild('content') nav: NavController;

  config = AppConfig.settings;
  permissions: SidebarPermissions = new SidebarPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  pages = [];

  initialSetupComplete: boolean;
  setupConfigured: boolean;
  sidebarCollapsed: boolean;
  canEnterData: boolean = false;
  dailyLogStale: boolean = false;
  disableDailyLogButton: boolean;
  implementerActive: boolean;
  isNonJackingVessel: boolean = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public navHelperService: NavHelperService,
    public authService: AuthService,
    public permissionService: PermissionsService,
    public app: App,
    private implementerService: ImplementerService,
    private idleService: IdleService,
    private vesselService: VesselService,
    private supportContextService: SupportContextService
  ) {
    this.navHelperService.currentSidebarCollapsed.subscribe(sidebarCollapsed => {
      this.sidebarCollapsed = sidebarCollapsed;
    });

    // setup configured for day
    this.navHelperService.currentSetupConfigured.subscribe(setupConfigured => {
      this.setupConfigured = setupConfigured;
    });

    // setup configured at all for any day
    this.navHelperService.currentInitialSetupComplete.subscribe(initialSetupComplete => {
      this.initialSetupComplete = initialSetupComplete;
    });

    this.navHelperService.canEnterData.subscribe(canEnterData => {
      this.canEnterData = canEnterData;
    });

    this.navHelperService.dailyLogStale.subscribe(dailyLogStale => {
      this.dailyLogStale = dailyLogStale;
    });
  }

  async ionViewDidEnter() {
    if (this.config.isImplementerEnvironment) {
      const activeImplementerSnapshot = await this.implementerService.getActiveSnapshotSummary(this.config.isVesselEnvironment);

      if (activeImplementerSnapshot) {
        this.implementerActive = true;
      }
    }

    Promise.all([
      this.vesselService.isCurrentVesselNonJacking(),
      this.permissionService.sidebarPermissions(),
      this.permissionService.lockedDayPermissions()
    ]).then(res => {
      this.isNonJackingVessel = res[0];
      this.permissions = res[1];
      this.lockedDayPermissions = res[2];
      this.pages = this.buildPages();
    });
  }

  private buildPages() {
    const pages = [
      {
        title: 'Implementer Navigator',
        component: ImplementerNavigatorPage,
        supportContextKey: SupportContextKey.ImplementerMode,
        active: this.permissions.canClickDailyLog
      },
      {
        title: 'Daily Log',
        component: DailyLogPage,
        supportContextKey: SupportContextKey.DailyLog,
        active: this.permissions.canClickDailyLog
      },
      {
        title: 'Daily Log Summary',
        component: DailyLogSummaryPage,
        supportContextKey: SupportContextKey.DailyLogSummary,
        active: this.permissions.canClickDailyLog
      },
      {
        title: 'Add to Daily Log', component: LogEventsPage,
        supportContextKey: SupportContextKey.AddToDailyLog,
        active: this.permissions.canClickAddToDailyLog && this.lockedDayPermissions.functionalPermissions.canAddRemoveOperations
      },
      this.sectionPage(PageValidationType.DailyLegLoad),
      this.sectionPage(PageValidationType.WeatherReading),
      {
        title: 'DPR Sections', component: DprSectionsEventsPage,
        supportContextKey: SupportContextKey.DprSections,
        active: ((!this.isNonJackingVessel && this.permissions.canClickConsumables))
          || this.permissions.canClickDailyObservations || this.permissions.canClickSafety || this.permissions.canClickVesselCrew
      },
      {
        title: 'Jacking Activities',
        component: JackingActivitiesRouterPage,
        supportContextKey: SupportContextKey.JackingActivities,
        active: this.permissions.canClickJacking
      },
      {
        title: 'Voyage Log',
        component: VoyageLogPage,
        supportContextKey: SupportContextKey.VoyageLog,
        active: this.permissions.canClickVoyageLog
      },
      {
        title: 'MoM',
        component: MorningMinutesPage,
        supportContextKey: SupportContextKey.MorningMinutes,
        active: this.permissions.canClickMorningMinutes
      },
      {
        title: 'Settings',
        component: NavSettingsSubmenuPage,
        active: this.permissions.canClickProjectDetails ||
          this.permissions.canClickAddToDailyLog ||
          this.permissions.canClickDailyLog
      },
      {title: 'Logout', component: null, active: true},
    ];

    if (!this.isNonJackingVessel) {
      return pages;
    }

    return pages.filter(page =>
      page.title !== 'Leg Loads' &&
      page.title !== 'Weather Log' &&
      page.title !== 'Jacking Activities' &&
      page.title !== 'Voyage Log'
    );
  }

  isDisabledByDailyLogStale(title: string): boolean {
    return this.dailyLogStale && (
      title === 'Add to Daily Log'
    );
  }

  private sectionPage(pageType: PageValidationType) {
    const section = menuSectionFor(pageType);
    return { ...section, active: this.permissions[section.permission] };
  }

  selectPage(page: any, title: string, supportContextKey?: SupportContextKey) {
    if (this.isDisabledByDailyLogStale(title)) {
      return;
    }

    if (title === 'Logout') {
      const user = this.authService.currentUser<User>();
      const userId = user && user.id ? cloneDeep(this.authService.currentUser<User>().id) : null;

      this.authService.logout();
      this.idleService.stopIdleTimer();
      this.app.getActiveNav().parent.push(LoginPage, {previousUser: userId});
      return;
    }

    // if the user doubleClicks the daily log by accident, we don't want to actually load it up twice
    // so set a 1 second timer where if the user clicks the daily log button again - nothing will happen
    // even though we use this bool to set it disabled, it will still appear enabled to the user
    if (page.name === 'DailyLogPage') {
      this.disableDailyLogButton = true;
      setTimeout(() => {
        this.disableDailyLogButton = false;
      }, 1000);
    }

    if (page.name === 'LogEventsPage') {
      if (supportContextKey) {
        this.supportContextService.setComponentContext(supportContextKey, title);
      }
      this.navCtrl.push(NavDailyLogSubmenuPage);
    } else if (page.name === 'DprSectionsEventsPage') {
      if (supportContextKey) {
        this.supportContextService.setComponentContext(supportContextKey, title);
      }
      this.navCtrl.push(NavDprSectionsSubmenuPage, null, {animate: false});
    } else if (page.name === 'NavSettingsSubmenuPage') {
      this.navCtrl.push(NavSettingsSubmenuPage, null, {animate: false});
    } else {
      this.navHelperService.navigateFromMenu({ page: page,
        supportContextKey: supportContextKey, supportContextLabel: title });
    }
  }

  // lists pages that are active irregardless of day being locked
  activePages(object: any): boolean {
    if (object == null) {
      return false;
    }

    switch (object.name) {
      case 'DailyLogSummaryPage':
      case 'DprSectionsEventsPage':
      case 'DailyLogPage':
      case 'ImplementerNavigatorPage':
      case 'MorningMinutesPage':
      case 'NavSettingsSubmenuPage':
        return true;
      case 'WeatherLogPage':
      case 'JackingActivitiesRouterPage':
      case 'VoyageLogPage':
      case 'DailyLegLoadsLogPage':
        return !this.isNonJackingVessel;
    }

    return false;
  }

  collapseSidebar() {
    this.navHelperService.toggleSidebarCollapse();
  }
}



// WEBPACK FOOTER //
// ./src/pages/nav/nav-menus/nav-main-menu/nav-main-menu.ts